/**
 * Existing backend roles ONLY.
 * Do not invent additional roles.
 */
export const ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  DEPARTMENT_ADMIN: 'department_admin',
  MANAGER: 'manager',
  TEAM_LEAD: 'team_lead',
  BDE: 'bde',
  DEVELOPER: 'developer',
  EMPLOYEE: 'employee',
  DRIVER: 'driver',
  FIELD_EXECUTIVE: 'field_executive',
  // userSchema.role allows 11 values (src/db-mongo.js). This list had 10, so
  // every `sales_coordinator` account — Rukmini (bdm6) among them — failed to
  // type as a Role, which is what made RoleContext and the nav helpers refuse
  // to compile. The label map below already carried it; the union had not caught up.
  SALES_COORDINATOR: 'sales_coordinator',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * Human label for every role the backend schema allows
 * (userSchema.role in src/db-mongo.js — 11 values).
 *
 * This is the canonical copy. Three screens each kept their own map before,
 * which is how `sales_coordinator` ended up rendering as a raw token in one of
 * them, and why the same role could read as "Business Development" here and
 * "Business Development Executive" there.
 *
 * Note ROLES above is missing `sales_coordinator`; that union is used in
 * exhaustive switches, so it is left alone rather than widened as a side
 * effect of this label map.
 */
export const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Owner',
  admin: 'Administrator',
  department_admin: 'Department Administrator',
  manager: 'Manager',
  team_lead: 'Team Lead',
  bde: 'Business Development',
  developer: 'Developer',
  employee: 'Employee',
  driver: 'Driver',
  field_executive: 'Field Executive',
  sales_coordinator: 'Sales Coordinator',
};

export const roleLabel = (role?: string | null) => (role ? ROLE_LABELS[role] ?? role : '—');
